/**
 * Theme Configuration System
 *
 * Provides semantic color mappings for light and dark themes.
 * Supports local overrides via themes/custom/ (gitignored).
 *
 * Design principles:
 * - Semantic naming by intent (accentMainFill, accentStroke) for proper contrast
 * - Simple flat structure for easy overrides
 * - Build-time merge of default + local configuration
 * - All theme values resolved at build time (SSR) for Gatsby
 */

import defaultTheme from '../config/themes/default/theme';

// Note: To use a custom logo, override via themes/custom/theme.js:
// import CustomLogo from '@assets/icons/custom-logo.svg'
// export default { logoComponent: CustomLogo }

// Deep merge helper for nested objects
const deepMerge = (target, source) => {
    const output = { ...target };

    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach((key) => {
            if (isObject(source[key])) {
                if (!(key in target)) {
                    output[key] = source[key];
                } else {
                    output[key] = deepMerge(target[key], source[key]);
                }
            } else {
                output[key] = source[key];
            }
        });
    }

    return output;
};

const isObject = (item) => {
    return item && typeof item === 'object' && !Array.isArray(item);
};

// Try to load custom theme config (webpack resolves this at build time)
let customTheme = {};
try {
    customTheme = require('../config/themes/custom/theme').default || {};
} catch (e) {
    // No custom config exists, use defaults only
}

/**
 * Apply theme by setting data-theme attribute
 * CSS variables are defined in themes/[custom|default]/colors.scss and respond to :root[data-theme]
 * @param {string} luminosity - The color scheme mode (e.g., 'dark', 'light')
 */
export const applyThemeCSSVariables = (luminosity) => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;

    // Set data-theme attribute for CSS selectors
    // This triggers :root[data-theme='light'] and :root[data-theme='dark'] in CSS
    root.setAttribute('data-theme', luminosity);

    // NOTE: All theme CSS variables are defined in themes/*/colors.scss
    // The CSS handles theme switching automatically based on the data-theme attribute
};

/**
 * Get the logo component (React component for SVG)
 * Checks for themes/custom/theme.js override first, then uses default
 * @returns {React.Component} The logo SVG component
 */
export const getLogo = () => {
    return customTheme.logoComponent || defaultTheme.logoComponent;
};
